/**
 * Custom hook to handle individual investor organization submission
 * Consolidates create and update org mutation logic with Regtank modal flow
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import { countryNameToCodeMap } from "@funded-here-interface/common/src/constant/country";
import useCreateOrg from "../../../hooks/useCreateOrg";
import useUpdateOrg from "../../../hooks/useUpdateOrg";
import {
  buildIndividualInvestorBankObject,
  buildIndividualInvestmentPreferences,
  buildCreateIndividualInvestorOrgData,
  buildUpdateIndividualInvestorOrgData,
  IndividualInvestorProfState,
  IndividualInvestorBankState,
  IndividualInvestorDocumentsUpload,
} from "../utils/individualInvestorOrgDataBuilder";
import { useRegtankValidation } from "../../Login/hooks/useRegtankValidation";
import { individualUserDetail } from "../../Login/hooks/useUserDataSync";

interface UseIndividualInvestorOrgSubmissionParams {
  setIsLoading: (loading: boolean) => void;
  onError: (error: Error) => void;
  setRegtankOnboardingUrl: (url: string) => void;
  handleNavigateNext: (
    userDetail: individualUserDetail,
    isValidOnboardingLink: boolean,
    openModal: () => void
  ) => "NO_ACTION_REQUIRED" | void;
}

/**
 * Success handler callback interface
 */
interface SuccessHandlerParams {
  regtankOnboardingFeatureFlag: boolean;
  regtankOnboarding: boolean;
  regtankRequest: any;
  declarationAISubmitted: boolean;
  openRegtankModal: () => void;
  isPendingResubmission: boolean;
}

/**
 * Hook to handle individual investor organization submission (create or update)
 */
export const useIndividualInvestorOrgSubmission = ({
  setIsLoading,
  onError,
  setRegtankOnboardingUrl,
  handleNavigateNext,
}: UseIndividualInvestorOrgSubmissionParams) => {
  const navigate = useNavigate();
  const { validateAndSetRegtankUrl } = useRegtankValidation();
  const createOrgMutation = useCreateOrg();
  const updateOrgMutation = useUpdateOrg();

  /**
   * Handle post-submission success with Regtank modal or navigation
   * Consolidates duplicate success handler logic
   */
  const handleSubmissionSuccess = useCallback(
    ({
      regtankOnboardingFeatureFlag,
      regtankOnboarding,
      regtankRequest,
      declarationAISubmitted,
      openRegtankModal,
      isPendingResubmission,
    }: SuccessHandlerParams) => {
      setIsLoading(false);

      const userDetail: individualUserDetail = {
        regtankOnboardingFeatureFlag,
        regtankOnboarding,
        regtankRequest,
      };
      // Validate and set Regtank URL if applicable
      const isValidOnboardingLink = regtankRequest?.length > 0
        ? validateAndSetRegtankUrl(userDetail, setRegtankOnboardingUrl).isValid
        : false;
      // Show Regtank modal if feature is enabled and onboarding required
      const result = handleNavigateNext(
        userDetail,
        isValidOnboardingLink,
        openRegtankModal
      );

      // Navigate to declaration page if not yet submitted
      if (!declarationAISubmitted && result) {
        navigate("/declaration-ai", {
          state: { isPendingResubmission },
        });
      }
    },
    [
      navigate,
      setIsLoading,
      handleNavigateNext,
      setRegtankOnboardingUrl,
      validateAndSetRegtankUrl,
    ]
  );

  /**
   * Submit individual investor organization (create or update based on KYC status)
   */
  const submitIndividualInvestorOrganization = useCallback(
    ({
      kycStatus,
      orgId,
      token,
      userFirstName,
      userLastName,
      profState,
      bankState,
      documentsUpload,
      regtankOnboardingFeatureFlag,
      regtankOnboarding,
      regtankRequest,
      declarationAISubmitted,
      openRegtankModal,
    }: {
      kycStatus: string | null;
      orgId: string | null;
      token: string;
      userFirstName: string;
      userLastName: string;
      profState: IndividualInvestorProfState;
      bankState: IndividualInvestorBankState;
      documentsUpload: IndividualInvestorDocumentsUpload;
      regtankOnboardingFeatureFlag: boolean;
      regtankOnboarding: boolean;
      regtankRequest: any;
      declarationAISubmitted: boolean;
      openRegtankModal: () => void;
    }) => {
      // Build bank object
      const bank = buildIndividualInvestorBankObject(bankState);

      // Build investment preferences
      const investmentPreferences = buildIndividualInvestmentPreferences(
        profState.investmentAmount
      );

      // Get country code
      let countryCode: string | null = null;
      if (profState.countryOfResidence) {
        countryCode =
          countryNameToCodeMap.get(profState.countryOfResidence) || null;
      }

      setIsLoading(true);

      const baseParams = {
        profState,
        bank,
        token,
        userFirstName,
        userLastName,
        countryCode,
        documentsUpload,
        investmentPreferences,
      };

      const successHandlerParams: SuccessHandlerParams = {
        regtankOnboardingFeatureFlag,
        regtankOnboarding,
        regtankRequest,
        declarationAISubmitted,
        openRegtankModal,
        isPendingResubmission: false,
      };

      // If KYC is pending resubmission, update existing org
      if (kycStatus === KYCStatus.PENDING_RESUBMISSION) {
        const updateData = buildUpdateIndividualInvestorOrgData(baseParams);

        updateOrgMutation.mutate(
          {
            id: orgId?.toString() || "",
            data: updateData,
          },
          {
            onSuccess: () => {
              handleSubmissionSuccess({
                ...successHandlerParams,
                isPendingResubmission: true,
              });
            },
            onError: (e) => {
              setIsLoading(false);
              onError(e as Error);
            },
          }
        );
      }
      // Otherwise, create new org
      else {
        const createData = buildCreateIndividualInvestorOrgData(baseParams);

        createOrgMutation.mutate(createData, {
          onSuccess: () => {
            handleSubmissionSuccess({
              ...successHandlerParams,
              isPendingResubmission: true,
            });
          },
          onError: (e) => {
            setIsLoading(false);
            onError(e as Error);
          },
        });
      }
    },
    [
      createOrgMutation,
      updateOrgMutation,
      setIsLoading,
      onError,
      handleSubmissionSuccess,
    ]
  );

  return { submitIndividualInvestorOrganization, handleSubmissionSuccess };
};
